import { useCallback, useMemo, useRef } from 'react';
import { DocumentReferenceObjectView, DocumentReferencesView } from 'api';
import DocumentPreviewDrawerTab from 'components/Drawer/document-preview-drawer-tab/DocumentPreviewDrawerTab';
import NavigationConfig, {
  DrawerSecondaryAction,
} from 'components/Drawer/tab-drawer/models/navigation-config.interface';
import { useDocuments, useGetDocumentsById, useLocalStorage, useResponse } from 'services/hooks';
import useGetFileIconByExtension from 'utilities/getFileIconByExtension/getFileIconByExtension';

export interface UseDocumentTabsOptions {
  storageKey: string;
  companyId: string;
  documentTabCustomActions?: DrawerSecondaryAction[];
  documentSourceProps?: {
    documentMetadata?: Map<number, number>;
  };
}

/**
 * Manages document preview tab lifecycle: localStorage persistence,
 * data fetching via React Query, and NavigationConfig generation.
 */
export default function useDocumentTabs({
  storageKey,
  companyId,
  documentTabCustomActions,
  documentSourceProps,
}: UseDocumentTabsOptions) {
  const [documentNavItems, setDocumentNavItems] = useLocalStorage<Record<string, string>>(storageKey, {});
  const { downloadDocument } = useDocuments();
  const downloadDocumentRef = useRef(downloadDocument);
  downloadDocumentRef.current = downloadDocument;
  const { errorNotification } = useResponse();
  const getFileIcon = useGetFileIconByExtension;
  const explicitlyClosedTabs = useRef<Set<string>>(new Set());

  const closeDocumentTab = useCallback(
    (tabId: string) => {
      explicitlyClosedTabs.current.add(tabId);
      setDocumentNavItems(prev => {
        const { [tabId]: removedTab, ...rest } = prev ?? {};
        return removedTab ? rest : rest;
      });
    },
    [setDocumentNavItems]
  );

  const openDocumentTab = useCallback(
    (document: DocumentReferencesView, reference: DocumentReferenceObjectView): string => {
      const docId = document.document_reference_id.toString();

      if (documentNavItems?.[docId]) {
        return docId;
      }

      explicitlyClosedTabs.current.delete(docId);
      setDocumentNavItems(prev => ({
        ...prev,
        [docId]: reference?.id?.toString() ?? docId,
      }));

      return docId;
    },
    [documentNavItems, setDocumentNavItems]
  );

  const openDocumentById = useCallback(
    (docId: string): string => {
      if (documentNavItems?.[docId]) {
        return docId;
      }
      explicitlyClosedTabs.current.delete(docId);
      setDocumentNavItems(prev => ({ ...prev, [docId]: docId }));
      return docId;
    },
    [documentNavItems, setDocumentNavItems]
  );

  const openDocumentIds = useMemo(() => Object.keys(documentNavItems ?? {}), [documentNavItems]);

  const handleDocumentLoadError = useCallback(
    (failedDocumentId: string) => {
      closeDocumentTab(failedDocumentId);
      errorNotification(`Error loading document for preview: ${failedDocumentId}`);
    },
    [closeDocumentTab, errorNotification]
  );

  const { data: documentsData, isLoading } = useGetDocumentsById({
    documentIds: openDocumentIds,
    onDocumentLoadError: handleDocumentLoadError,
  });

  const documentTabConfigs = useMemo((): NavigationConfig[] => {
    if (!documentsData) {
      return [];
    }

    return Object.values(documentsData)
      .filter(doc => !explicitlyClosedTabs.current.has(doc.document_reference_id.toString()))
      .map(doc => {
        const docId = doc.document_reference_id.toString();
        const fileIcon = getFileIcon(doc.file.file_type);
        const citationNumber = documentSourceProps?.documentMetadata?.get(doc.document_reference_id);
        const tabTitle = citationNumber ? `[${citationNumber}] - ${doc.file.filename}` : doc.file.filename;

        const secondaryActions: DrawerSecondaryAction[] = [
          {
            title: 'Download original',
            onClick: async () => {
              await downloadDocumentRef.current(doc);
            },
          },
          ...(documentTabCustomActions?.map(action => ({
            ...action,
            onClick: async () => action.onClick(doc),
          })) ?? []),
        ];

        return {
          tabContent: <DocumentPreviewDrawerTab document={doc} companyId={companyId} />,
          tabTitle,
          uniqueId: docId,
          icon: fileIcon,
          secondaryActions,
        };
      });
  }, [documentsData, getFileIcon, documentSourceProps, companyId, documentTabCustomActions]);

  return {
    documentTabConfigs,
    isLoading,
    openDocumentTab,
    openDocumentById,
    closeDocumentTab,
    openDocumentIds,
  };
}
